import { Select, TreeSelect } from "antd";
import type { JSX, ReactNode } from "react";
import { useIntl } from "react-intl";
import { StyledDimBackground, StyledLoader } from "~components/CustomSelect/CustomSelect.styles.ts";
import type { LabelValue } from "~types/labelValue";
import classNames from "~utils/classNames";
import { PermissionScopeTypes } from "../../enums/permissionScopeTypes";
import type { EntitySelectOption, PermissionScopeTreeNode } from "../types";
import SelectPermissionEntities from "./SelectPermissionEntities";

import "./SelectPermissionScope.scss";

type ScopeValueTitleProps = {
  scopeType: PermissionScopeTypes;
  allowedScope: string | null;
  title: string | JSX.Element;
};

const ScopeValueTitle = ({ title, allowedScope, scopeType }: ScopeValueTitleProps) => {
  const { formatMessage } = useIntl();
  let selectChildrenText: ReactNode;

  if (!title) {
    return "";
  }

  if (
    (allowedScope === PermissionScopeTypes.Season && scopeType !== PermissionScopeTypes.Season) ||
    (allowedScope === PermissionScopeTypes.Competition && scopeType === PermissionScopeTypes.League)
  ) {
    selectChildrenText = formatMessage(
      { id: "form.help.permissions.role.scopeValues.select.all.in.scope" },
      {
        scopeTypeToSelect: formatMessage({ id: allowedScope.toLowerCase() }),
        scopeTypeToSelectFrom: formatMessage({ id: scopeType.slice(0, -1).toLowerCase() }),
      },
    );
  }

  if (selectChildrenText) {
    return (
      <>
        <span className="select-children-title">{title}</span>
        <span className="select-children-subtitle">{`(${selectChildrenText})`}</span>
      </>
    );
  }

  return <span style={{ fontWeight: "normal" }}>{title}</span>;
};

type SelectPermissionScopeProps = {
  leagueTreeData: PermissionScopeTreeNode[];
  entityGroupOptions: LabelValue[];
  entityOptions: EntitySelectOption[];
  onChange?: (values: LabelValue[] | null) => void;
  value?: LabelValue[];
  disabled: boolean;
  isLoading: boolean;
  allowedScope: string | null;
  placeholder: string;
};

const SelectPermissionScope = ({
  entityOptions,
  entityGroupOptions,
  leagueTreeData,
  onChange,
  value,
  disabled,
  isLoading,
  allowedScope,
  placeholder,
}: SelectPermissionScopeProps) => {
  if (allowedScope === PermissionScopeTypes.EntityGroup) {
    return (
      <Select value={value} mode="multiple" showSearch options={entityGroupOptions} disabled={disabled} labelInValue onChange={onChange} />
    );
  }

  if (allowedScope === PermissionScopeTypes.Entity) {
    return <SelectPermissionEntities disabled={disabled} options={entityOptions} value={value} onChange={onChange} />;
  }

  return (
    <TreeSelect
      size="large"
      autoClearSearchValue={false}
      classNames={{
        popup: {
          // if we only show leagues (which means tree has only one level)
          // we need to adjust styles to make it look like a regular select element
          root: classNames("permissions-scope-select", allowedScope === PermissionScopeTypes.League ? "is-one-level" : ""),
        },
      }}
      key={allowedScope}
      placeholder={placeholder}
      showSearch
      treeDefaultExpandAll
      disabled={disabled}
      listHeight={400}
      value={value}
      treeTitleRender={(item: PermissionScopeTreeNode) => (
        <ScopeValueTitle title={item.title} scopeType={item.scopeType} allowedScope={allowedScope} />
      )}
      allowClear
      multiple
      onChange={onChange}
      treeData={leagueTreeData}
      labelInValue
      treeCheckable
      filterTreeNode
      treeNodeFilterProp="title"
      showCheckedStrategy={TreeSelect.SHOW_CHILD}
      popupRender={(menu) => (
        <>
          {isLoading && (
            <>
              <StyledDimBackground />
              <StyledLoader />
            </>
          )}
          {menu}
        </>
      )}
    />
  );
};

export default SelectPermissionScope;
